import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { firstValueFrom, Observable, of, switchMap, timeout } from 'rxjs';
import { DocumentPipe } from '../shared/pipes/document.pipe';
import { DataService } from './data.service';
import { FbImageDownloaderService } from './fb-image-downloader.service';

export type StorageUploadIntent = {
  scope: 'company-logo' | 'company-associate-logo' | 'user-photo' | 'client-photo' | 'supplier-photo' | 'checklist-item-media' | 'checklist-model-breakdown' | 'checklist-model-signature' | 'checklist-model-photo' | 'checklist-model-default-breakdown' | 'os-breakdowns-image' | 'os-breakdowns-signature' | 'os-breakdowns-damage-attachment';
  companyId?: string;
  userId?: string;
  personId?: string;
  osId?: string;
  checklistId?: string;
  checklistItemId?: string;
  checklistModelId?: string;
  damageIndex?: number;
  mediaType?: 'image' | 'video' | 'audio';
  fileIndex?: number;
};

type PresignResponse = {
  items: Array<{
    key: string;
    url?: string;
    expiresAt?: string;
    error?: string;
  }>;
};

type UploadResponse = {
  key: string;
  url: string;
  expiresAt: string;
};

type CachedUrl = {
  url: string;
  expiresAt: number;
};

@Injectable({ providedIn: 'root' })
export class StorageUrlService {
  private readonly http = inject(HttpClient);
  private readonly dataService = inject(DataService);
  private readonly fbImageDownloader = inject(FbImageDownloaderService);
  private readonly cache = new Map<string, CachedUrl>();
  private readonly inflight = new Map<string, Promise<void>>();
  private readonly refreshSkewMs = 60 * 1000;

  resolve(value: string): string {
    const normalized = this.normalize(value);
    if (!normalized || this.isPassthrough(normalized)) {
      return normalized;
    }

    const cached = this.cache.get(normalized);
    if (cached && cached.expiresAt - this.refreshSkewMs > Date.now()) {
      return cached.url;
    }

    return '';
  }

  preload(values: string[], cnpj?: string): Observable<void> {
    return this.preloadFromEndpoint(values, `${environment.mkgoURL}/api/v1/storage/presign`, cnpj);
  }

  preloadForCurrentUser(values: string[]): Observable<void> {
    return this.preloadFromEndpoint(values, `${environment.mkgoURL}/api/v1/storage/presign-user`, undefined, 'current-user');
  }

  private preloadFromEndpoint(values: string[], endpoint: string, cnpj?: string, cacheNamespace?: string): Observable<void> {
    const keys = [...new Set((values || [])
      .map(value => this.normalize(value))
      .filter(value => value && !this.isPassthrough(value))
      .filter(value => !this.hasFreshCache(value)))];

    if (!keys.length) {
      return of(void 0);
    }

    const mapKey = `${cacheNamespace || cnpj || this.dataService.company?.cnpj || ''}:${keys.sort().join('|')}`;
    const existing = this.inflight.get(mapKey);
    if (existing) {
      return new Observable<void>(subscriber => {
        existing.then(() => {
          subscriber.next();
          subscriber.complete();
        }).catch(error => subscriber.error(error));
      });
    }

    const promise = firstValueFrom(this.presign(endpoint, keys, cnpj)).then(response => {
      for (const item of response.items || []) {
        if (item.key && item.url && item.expiresAt) {
          this.cacheResponse(item.key, item.url, item.expiresAt);
        }
      }
    }).finally(() => this.inflight.delete(mapKey));

    this.inflight.set(mapKey, promise);

    return new Observable<void>(subscriber => {
      promise.then(() => {
        subscriber.next();
        subscriber.complete();
      }).catch(error => subscriber.error(error));
    });
  }

  getBlob(value: string, cnpj?: string): Observable<Blob> {
    const normalized = this.normalize(value);
    if (!normalized) {
      return of(null);
    }

    if (this.isRuntimeKey(normalized)) {
      const url = `${environment.mkgoURL}/api/v1/storage/blob`;
      return this.options(cnpj).pipe(
        switchMap(options => this.http.post(url, { key: normalized }, { ...options, responseType: 'blob' })),
        timeout(10000)
      );
    }

    if (this.isFirebaseUrl(normalized)) {
      return this.fbImageDownloader.getBlob({ url: normalized, cnpj }) as Observable<Blob>;
    }

    return this.http.get(normalized, { responseType: 'blob' }).pipe(timeout(10000));
  }

  async upload(file: Blob, intent: StorageUploadIntent, cnpj?: string): Promise<UploadResponse> {
    const hasCompanyContext = Boolean(cnpj || this.dataService.company?.cnpj);
    const url = intent.scope === 'user-photo' && !hasCompanyContext
      ? `${environment.mkgoURL}/api/v1/storage/upload-user`
      : `${environment.mkgoURL}/api/v1/storage/upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('intent', JSON.stringify(intent));

    const options = this.companyOnlyOptions(cnpj);
    const response = await firstValueFrom(this.http.post<UploadResponse>(url, formData, options));
    this.cacheResponse(response.key, response.url, response.expiresAt);
    return response;
  }

  cacheResponse(key: string, url: string, expiresAt: string): void {
    if (!key || !url || !expiresAt) {
      return;
    }

    this.cache.set(key, {
      url,
      expiresAt: new Date(expiresAt).getTime(),
    });
  }

  private presign(url: string, keys: string[], cnpj?: string): Observable<PresignResponse> {
    return this.options(cnpj).pipe(
      switchMap(options => this.http.post<PresignResponse>(url, { keys }, options)),
      timeout(10000)
    );
  }

  private options(cnpj?: string): Observable<{ headers: HttpHeaders }> {
    return of(this.companyOnlyOptions(cnpj).headers).pipe(
      switchMap(headers => of({ headers: headers.append('Content-Type', 'application/json') }))
    );
  }

  private companyOnlyOptions(cnpj?: string): { headers: HttpHeaders } {
    const companyCnpj = cnpj || this.dataService.company?.cnpj || '';
    let headers = new HttpHeaders();

    if (companyCnpj) {
      headers = headers.append('Company-Cnpj', new DocumentPipe().transform(companyCnpj));
    }

    return {
      headers
    };
  }

  private hasFreshCache(key: string): boolean {
    const cached = this.cache.get(key);
    return Boolean(cached && cached.expiresAt - this.refreshSkewMs > Date.now());
  }

  private normalize(value: string): string {
    return (value || '').trim();
  }

  private isPassthrough(value: string): boolean {
    return value.startsWith('blob:') || value.startsWith('data:') || /^https?:\/\//i.test(value);
  }

  private isFirebaseUrl(value: string): boolean {
    return /^https?:\/\/firebasestorage\.googleapis\.com\//i.test(value);
  }

  private isRuntimeKey(value: string): boolean {
    return Boolean(value) && !this.isPassthrough(value);
  }
}
